import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

import { IStorageAdapter } from '@core/interfaces/storage-adapter.interface';

/**
 * Adapter backed by @ionic/storage (SQLite on native, IndexedDB/localStorage on web).
 * Reads are synchronous via an in-memory cache that is hydrated during app init.
 * Writes are applied to the cache immediately and persisted to Ionic Storage asynchronously.
 * Call init() via APP_INITIALIZER before the app becomes active.
 */
@Injectable({ providedIn: 'root' })
export class IonicStorageAdapter implements IStorageAdapter {
  readonly #cache = new Map<string, unknown>();

  constructor(private readonly storage: Storage) {}

  async init(): Promise<void> {
    await this.storage.create();
    const keys = await this.storage.keys();
    await Promise.all(
      keys.map(async key => {
        const value = await this.storage.get(key);
        this.#cache.set(key, value);
      })
    );
  }

  get<T>(key: string): T | null {
    return this.#cache.has(key) ? (this.#cache.get(key) as T) : null;
  }

  set(key: string, value: unknown): void {
    this.#cache.set(key, value);
    void this.storage.set(key, value);
  }

  remove(key: string): void {
    this.#cache.delete(key);
    void this.storage.remove(key);
  }

  clear(): void {
    this.#cache.clear();
    void this.storage.clear();
  }

  has(key: string): boolean {
    return this.#cache.has(key);
  }
}
